import * as v from 'valibot'

//
// Structural Firestore shapes and their valibot schemas. The single home for
// these in shared/: every domain that parses a Firestore document imports from
// here. Imports valibot only, never a domain.
//
// Declared structurally rather than imported from a vendor SDK because the
// browser gets `firebase` and Node gets `firebase-admin`, and shared/ compiles
// for both. Consumers match by shape.
//

/**
 * The minimal Firestore Timestamp surface the snapshot envelope exposes.
 *
 * @remarks
 *   Structural, so it accepts both the browser SDK and `firebase-admin`
 *   timestamp instances.
 */
export interface FirestoreTimestampLike {
  seconds: number
  nanoseconds: number
  toDate: () => Date
}

/**
 * A Firestore Timestamp, validated structurally and converted to a `Date`.
 *
 * @remarks
 *   Accepts both the browser SDK and `firebase-admin` timestamp instances.
 *   Deliberately not `v.object(...)`: that returns a fresh plain object holding
 *   only the declared keys, so `toDate` would run with `this` pointing at the
 *   copy. The web SDK's `toDate` calls `this.toMillis()`, which the copy lacks.
 *   `v.custom` checks the shape and passes the original instance through.
 */
export const FirestoreTimestampSchema = v.pipe(
  v.custom<FirestoreTimestampLike>(isFirestoreTimestampLike, 'Invalid Firestore Timestamp'),
  v.transform((timestamp) => timestamp.toDate()),
)

function isFirestoreTimestampLike(input: unknown): input is FirestoreTimestampLike {
  if (typeof input !== 'object' || input === null) return false
  const candidate = input as Record<string, unknown>
  return (
    typeof candidate['seconds'] === 'number' &&
    typeof candidate['nanoseconds'] === 'number' &&
    typeof candidate['toDate'] === 'function'
  )
}

/**
 * The minimal document-snapshot surface a Firestore document parser reads.
 *
 * @remarks
 *   Declared structurally rather than using `~shared/types`'s `DocumentSnapshot`:
 *   that type's `exists?: boolean` clashes with the web SDK's `exists()` type
 *   predicate, so the Firebase `QueryDocumentSnapshot` is not assignable to it.
 *   Only `id`, `data()` and the optional metadata are needed here.
 */
export interface FirestoreDocumentSnapshot {
  id: string
  data: () => unknown
  createTime?: FirestoreTimestampLike | undefined
  updateTime?: FirestoreTimestampLike | undefined
}
